import { X, Crown, Users, Mail, GraduationCap, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

interface TeamMember {
  id: string;
  name: string;
  avatar: string;
  role?: string;
  grade?: string;
  email?: string;
  jobRole?: string;
  status?: "Active" | "Pending";
}

interface ViewDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamName: string;
  hackathonName: string;
  leader: TeamMember;
  members: TeamMember[];
  status: "Selected" | "Under Review" | "Registered" | string;
}

const statusStyles = {
  Selected: {
    bg: "bg-[#46C9BC]",
    text: "text-white",
    headerBg: "bg-[#0058ab]",
    lightBg: "bg-[#E8F1FF]",
  },
  "Under Review": {
    bg: "bg-[#F4B400]",
    text: "text-white",
    headerBg: "bg-[#0058ab]",
    lightBg: "bg-[#E8F1FF]",
  },
  Registered: {
    bg: "bg-[#1A73E8]",
    text: "text-white",
    headerBg: "bg-[#0058ab]",
    lightBg: "bg-[#E8F1FF]",
  },
};

// ✅ NEW: Safe fallback
function getSafeStatus(status: string) {
  if (!status) return "Registered";

  const normalized = status.trim().toLowerCase();

  if (normalized === "selected") return "Selected";
  if (normalized === "under review" || normalized === "under_review") return "Under Review";
  if (normalized === "registered") return "Registered";

  // fallback
  return "Registered";
}

export function ViewDetailsModal({
  isOpen,
  onClose,
  teamName,
  hackathonName,
  leader,
  members,
  status,
}: ViewDetailsModalProps) {
  
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // ✅ Apply safe normalized status
  const safeStatus = getSafeStatus(status);

  // ✅ Guaranteed never undefined now
  const currentStatus = statusStyles[safeStatus];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-40"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white shadow-2xl border border-[#e4e8ee]
                         w-[672px] rounded-[16px] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* ✅ HEADER */}
              <div className={`${currentStatus.headerBg} px-8 py-4 text-white relative`}>
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-[16px]"
                >
                  <X className="w-5 h-5" />
                </button>

                <h2 className="font-medium text-[24px]">{teamName}</h2>
                <p className="text-white/90 text-[14px] font-medium mb-3">
                  {hackathonName}
                </p>

                <span
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-normal 
                  ${currentStatus.bg} ${currentStatus.text}`}
                >
                  {safeStatus}
                </span>
              </div>

              {/* ✅ CONTENT */}
              <div className="overflow-y-auto h-[calc(497px-150px)] bg-[#F7F9FC]">
                <div className="p-6 space-y-4">

                  {/* Leader */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="w-4 h-4 text-[#0058ab]" />
                      <h3 className="font-bold text-[16px]">Team Leader</h3>
                    </div>

                    <div className={`${currentStatus.lightBg} rounded-[16px] p-4 border`}>
                      <div className="flex items-start gap-4">
                        <img
                          src={leader.avatar}
                          alt={leader.name}
                          className="w-14 h-14 rounded-full object-cover ring-4 ring-white shadow-md"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[16px] mb-2">{leader.name}</p>

                          {leader.email && (
                            <div className="flex items-center gap-2 mb-2">
                              <Mail className="w-4 h-4 text-[#0058ab]" />
                              <a
                                href={`mailto:${leader.email}`}
                                className="text-[13px] text-[#0058ab] hover:underline truncate"
                              >
                                {leader.email}
                              </a>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2">
                            {leader.role && (
                              <div className="flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-lg shadow-sm">
                                <Briefcase className="w-3.5 h-3.5 text-[#1A73E8]" />
                                <span className="text-[13px] font-semibold">{leader.role}</span>
                              </div>
                            )}

                            {leader.grade && (
                              <div className="flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-lg shadow-sm">
                                <GraduationCap className="w-3.5 h-3.5 text-[#2BC6E9]" />
                                <span className="text-[13px] font-semibold">
                                  Grade {leader.grade}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t" />

                  {/* Members */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#0058ab]" />
                        <h3 className="font-bold text-[16px]">Team Members</h3>
                      </div>
                      <span className="bg-[#0058ab] text-white px-3 py-1 rounded-lg text-[12px]">
                        {members.length} {members.length === 1 ? "Member" : "Members"}
                      </span>
                    </div>

                    {members.length === 0 ? (
                      <div className="bg-white rounded-[16px] border p-8 text-center">
                        <Users className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                        <p>No team members yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {members.map((member, index) => (
                          <motion.div
                            key={member.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white rounded-[16px] border p-4 hover:shadow-md"
                          >
                            <div className="flex items-start gap-4">
                              <img
                                src={member.avatar}
                                alt={member.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-[14px] mb-1">
                                  {member.name}
                                </p>

                                {member.email && (
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-3.5 h-3.5 text-[#0058ab]" />
                                    <a
                                      href={`mailto:${member.email}`}
                                      className="text-[13px] text-[#0058ab] hover:underline truncate"
                                    >
                                      {member.email}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t px-6 py-3 bg-white flex justify-end">
                <button
                  onClick={onClose}
                  className="bg-[#0058ab] text-white py-2 px-6 rounded-[16px]"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}